import { defineAsyncComponent } from "vue";
import MkWaitingDialog from "@/components/MkWaitingDialog.vue";
import { $tr } from "@/i18n";
import { popup } from "@/dialog-legacy";
import { openModal } from "@/composables/modal";

export type MessageModel =
    | {
          type: "save-nosave-cancel";
          destructiveOptions?: MessageModelResponse["save-nosave-cancel"][];
      }
    | {
          type: "yes-no";
          destructiveOptions?: MessageModelResponse["yes-no"][];
      }
    | {
          type: "ok-cancel";
          destructiveOptions?: MessageModelResponse["ok-cancel"][];
      }
    | {
          type: "ok";
      };

export type MessageModelResponse = {
    "save-nosave-cancel": "save" | "nosave" | "cancel";
    "yes-no": "yes" | "no" | "cancel";
    "ok-cancel": "ok" | "cancel";
    ok: void;
};

export async function openMessageBox<const T extends MessageModel>(
    model: MessageModel,
    props: {
        kind?: "error" | "info" | "success" | "warning" | "question";
        title?: string;
        text?: string;
    },
    options?: {
        width?: number;
        height?: number;
    },
): Promise<MessageModelResponse[T["type"]]> {
    const { resolve, promise, reject } =
        Promise.withResolvers<MessageModelResponse[T["type"]]>();

    const modalClose = openModal(
        {
            dismissable: true,
        },
        defineAsyncComponent({
            loader: () =>
                import("@/components/layout/MagDialogMessageView.vue"),
        }),
        ({ onClose }) => ({
            onDmvButton: (bttn) => {
                resolve(bttn as MessageModelResponse[T["type"]]);
                onClose();
            },
            model,
            width: 700,
            height: 500,
            ...(options ?? {}),
            ...props,
        }),
    );

    modalClose.then(reject);

    return await promise;
}

export async function messageAlert(props: {
    type?: "error" | "info" | "success" | "warning" | "question";
    title?: string | null;
    text?: string | null;
}): Promise<void> {
    if (props.title == null && props.type === "error") {
        props.title = $tr("somethingHappened");
    }

    await openMessageBox(
        {
            type: "ok",
        },
        {
            kind: props.type,
            text: props.text ?? undefined,
            title: props.title ?? undefined,
        },
    ).then();
}

export async function messageConfirm(props: {
    type: "error" | "info" | "success" | "warning" | "question";
    title?: string | null;
    text?: string | null;
    okText?: string;
    cancelText?: string;
    destructive?: boolean;
}): Promise<"ok" | "cancel"> {
    return await openMessageBox(
        {
            type: "ok-cancel",
            destructiveOptions: props.destructive ? ["ok"] : [],
        },
        {
            kind: props.type,
            text: props.text ?? undefined,
            title: props.title ?? undefined,
        },
    );
}

export async function messageConfirmLegacy(props: {
    type: "error" | "info" | "success" | "warning" | "question";
    title?: string | null;
    text?: string | null;
    okText?: string;
    cancelText?: string;
    destructive?: boolean;
}): Promise<{ canceled: boolean }> {
    return await messageConfirm(props).then((res) => {
        return { canceled: res !== "ok" };
    });
}

export function inputText(props: {
    type?: "text" | "email" | "password" | "url" | "search";
    title?: string | null;
    text?: string | null;
    placeholder?: string | null;
    autocomplete?: string;
    default?: string | null;
    minLength?: number;
    maxLength?: number;
}): Promise<
    | { canceled: true; result: undefined }
    | {
          canceled: false;
          result: string;
      }
> {
    return new Promise((resolve, reject) => {
        popup(
            defineAsyncComponent({
                loader: () => import("@/components/MkDialog.vue"),
                loadingComponent: MkWaitingDialog,
                delay: 1000,
            }),
            {
                title: props.title,
                text: props.text,
                input: {
                    type: props.type,
                    placeholder: props.placeholder,
                    autocomplete: props.autocomplete,
                    default: props.default,
                    minLength: props.minLength,
                    maxLength: props.maxLength,
                },
            },
            {
                done: (result) => {
                    resolve(result ? result : { canceled: true });
                },
            },
            "closed",
        );
    });
}

export function inputParagraph(props: {
    title?: string | null;
    text?: string | null;
    placeholder?: string | null;
    default?: string | null;
}): Promise<
    | { canceled: true; result: undefined }
    | {
          canceled: false;
          result: string;
      }
> {
    return new Promise((resolve, reject) => {
        popup(
            defineAsyncComponent({
                loader: () => import("@/components/MkDialog.vue"),
                loadingComponent: MkWaitingDialog,
                delay: 1000,
            }),
            {
                title: props.title,
                text: props.text,
                input: {
                    type: "paragraph",
                    placeholder: props.placeholder,
                    default: props.default,
                },
            },
            {
                done: (result) => {
                    resolve(result ? result : { canceled: true });
                },
            },
            "closed",
        );
    });
}

export function inputNumber(props: {
    title?: string | null;
    text?: string | null;
    placeholder?: string | null;
    default?: number | null;
    autocomplete?: string;
}): Promise<
    | { canceled: true; result: undefined }
    | {
          canceled: false;
          result: number;
      }
> {
    return new Promise((resolve, reject) => {
        popup(
            defineAsyncComponent({
                loader: () => import("@/components/MkDialog.vue"),
                loadingComponent: MkWaitingDialog,
                delay: 1000,
            }),
            {
                title: props.title,
                text: props.text,
                input: {
                    type: "number",
                    placeholder: props.placeholder,
                    autocomplete: props.autocomplete,
                    default: props.default,
                },
            },
            {
                done: (result) => {
                    resolve(result ? result : { canceled: true });
                },
            },
            "closed",
        );
    });
}
